import { FlaskConical, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SandboxModeToggleProps {
  /** Show label text */
  showLabel?: boolean;
  /** Compact mode (icon only) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SandboxModeToggle - Allows users to toggle sandbox/development mode.
 *
 * When sandbox mode is enabled:
 * - Hard delete operations are allowed (instead of archive-only)
 * - Test data can be created and deleted freely
 * - Useful for trial accounts and onboarding testing
 */
export function SandboxModeToggle({
  showLabel = true,
  compact = false,
  className,
}: SandboxModeToggleProps) {
  const utils = trpc.useUtils();

  const { data: settings, isLoading } = trpc.userSettings.get.useQuery();

  const toggleMutation = trpc.userSettings.toggleSandboxMode.useMutation({
    onSuccess: data => {
      // Update the cached settings
      utils.userSettings.get.setData(undefined, old => ({
        ...old!,
        sandboxMode: data.sandboxMode,
      }));
    },
  });

  const sandboxMode = settings?.sandboxMode ?? false;

  const handleToggle = () => {
    toggleMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleToggle}
              disabled={toggleMutation.isPending}
              className={cn(
                "inline-flex items-center justify-center rounded-md p-2 transition-colors",
                sandboxMode
                  ? "bg-warning/10 text-warning hover:bg-warning/20"
                  : "text-muted-foreground hover:bg-muted",
                className
              )}
            >
              {toggleMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FlaskConical className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>
              Sandbox Mode: <strong>{sandboxMode ? "ON" : "OFF"}</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              {sandboxMode
                ? "Hard delete enabled, test freely"
                : "Production mode, archive-only deletes"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex items-center gap-2">
        <FlaskConical
          className={cn(
            "h-4 w-4",
            sandboxMode ? "text-warning" : "text-muted-foreground"
          )}
        />
        {showLabel && (
          <Label
            htmlFor="sandbox-mode"
            className="cursor-pointer text-sm font-medium"
          >
            Sandbox Mode
          </Label>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="sandbox-mode"
          checked={sandboxMode}
          onCheckedChange={handleToggle}
          disabled={toggleMutation.isPending}
        />
        {sandboxMode && (
          <Badge
            variant="outline"
            className="text-xs bg-warning/10 text-warning border-[var(--warning)]/30"
          >
            Testing
          </Badge>
        )}
      </div>

      {toggleMutation.isPending && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

/**
 * Hook to check if sandbox mode is enabled
 */
export function useSandboxMode() {
  const { data: settings } = trpc.userSettings.get.useQuery();
  return settings?.sandboxMode ?? false;
}
